import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { validatePlate, normalizePlate } from "@/lib/api";

/**
 * PlateInput: input targa con validazione formato italiano (AA123BB · PA121234 · X0A1B2).
 *
 * Props:
 * - value: string (targa corrente, eventualmente non normalizzata)
 * - onChange: funzione (string) — chiamata con la targa normalizzata maiuscola
 * - overrideFlag: boolean — true se l'utente ha forzato l'accettazione di un formato non standard
 * - onOverrideChange: funzione (boolean) — callback al toggle del flag
 * - testid: prefisso per data-testid (es. "qd-plate" → input testid, "qd-plate-override" → checkbox)
 * - placeholder, className: pass-through
 *
 * Note di UX:
 * - In caso di formato non valido: bordo rosso + messaggio sotto il campo
 * - Compare automaticamente la checkbox "Formato speciale" per consentire override
 * - Quando il flag è attivato, lo stile rosso si attenua (giallo) e il messaggio diventa avviso
 */
export function PlateInput({ value, onChange, overrideFlag = false, onOverrideChange, testid = "plate", placeholder = "AA123BB", className = "", required = false, disabled = false }) {
  const raw = value || "";
  const validation = useMemo(() => validatePlate(raw), [raw]);
  const isEmpty = !normalizePlate(raw);
  const isInvalid = !isEmpty && !validation.ok && !overrideFlag;
  const isOverridden = !isEmpty && !validation.ok && overrideFlag;

  return (
    <div className="space-y-1">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <Input
          data-testid={testid}
          value={raw}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            const normalized = normalizePlate(e.target.value);
            onChange?.(normalized);
          }}
          className={`uppercase font-mono tracking-widest text-base w-full sm:flex-1 ${
            isInvalid
              ? "border-rose-400 bg-rose-50/40 focus-visible:border-rose-500"
              : isOverridden
                ? "border-amber-400 bg-amber-50/40 focus-visible:border-amber-500"
                : (validation.ok ? "border-emerald-300 bg-emerald-50/30" : "")
          } ${className}`}
          required={required}
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck="false"
          maxLength={10}
        />
        {!isEmpty && !validation.ok && (
          <label className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 cursor-pointer select-none whitespace-nowrap px-2 py-1 rounded bg-amber-50 border border-amber-200" title="Spunta per accettare un formato di targa non standard (es. targa estera o di prova)">
            <input
              type="checkbox"
              data-testid={`${testid}-override`}
              checked={!!overrideFlag}
              onChange={(e) => onOverrideChange?.(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Formato speciale
          </label>
        )}
      </div>
      {isInvalid && (
        <p className="text-[10px] text-rose-600 leading-snug" data-testid={`${testid}-error`}>
          ⚠️ {validation.message}
        </p>
      )}
      {isOverridden && (
        <p className="text-[10px] text-amber-700 leading-snug" data-testid={`${testid}-warning`}>
          ⚠️ Formato non standard accettato per scelta operatore.
        </p>
      )}
      {validation.ok && !isEmpty && (
        <p className="text-[10px] text-emerald-700 leading-snug" data-testid={`${testid}-ok`}>
          ✓ Targa {validation.type === "new" ? "auto/moto (nuovo formato)" : validation.type === "old" ? "vecchio formato" : validation.type === "moped" ? "ciclomotore" : "valida"}
        </p>
      )}
    </div>
  );
}
